import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { ethers } from "ethers";
import { goBack } from "../store/slices/pageSlice";
import Header from "../components/header/Header";
import BalanceActionsBar from "../components/BalanceActionsBar";
import getIcon from "../utils/Icon";
import walletIcon from "../assets/icon/wallet.png";
import userApi from "../api/userApi";

// ERC-20 minimal ABI
const ERC20_ABI = [
    'function balanceOf(address) view returns (uint256)',
    'function decimals() view returns (uint8)',
    'function symbol() view returns (string)',
];

// Token config per chainId.
// На проде просто добавить новые chainId с реальными адресами контрактов.
const TOKEN_CONFIG = {
    // Hardhat local (31337 = 0x7a69)
    '31337': [
        { symbol: 'ETH',  name: 'Ethereum',  icon: 'ETH',  native: true },
        { symbol: 'USDT', name: 'Mock USDT', icon: 'USDT', address: '0x5FbDB2315678afecb367f032d93F642f64180aa3' },
    ],
    // Ethereum mainnet — добавишь реальные адреса позже
    '1': [
        { symbol: 'ETH',  name: 'Ethereum', icon: 'ETH',  native: true },
        { symbol: 'USDT', name: 'Tether',   icon: 'USDT', address: '0xdAC17F958D2ee523a2206206994597C13D831ec7' },
    ],
    // BSC mainnet
    '56': [
        { symbol: 'BNB',  name: 'BNB',       icon: 'BNB',  native: true },
        { symbol: 'USDT', name: 'Tether',     icon: 'USDT', address: '0x55d398326f99059fF775485246999027B3197955' },
    ],
};

function fmt(val, decimals = 4) {
    const n = parseFloat(val);
    if (isNaN(n)) return '0';
    return n.toFixed(decimals).replace(/\.?0+$/, '') || '0';
}

function BalanceItem({ icon, mini, max, count, loading }) {
    return (
        <div className="flex items-center justify-between py-3 border-b border-white/[0.07] last:border-0">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                    <img width={22} height={22} src={getIcon(icon)} alt={mini} className="object-contain" />
                </div>
                <div className="flex flex-col leading-tight">
                    <span className="text-white text-[14px] font-medium">{mini}</span>
                    <span className="text-white/40 text-[11px]">{max}</span>
                </div>
            </div>
            {loading ? (
                <div className="w-12 h-4 rounded-full bg-white/10 animate-pulse" />
            ) : (
                <span className="text-white/85 text-[15px] font-semibold tabular-nums">{count}</span>
            )}
        </div>
    );
}

export default function BalanceLayout() {
    const dispatch = useDispatch();
    const [connectedWallet, setConnectedWallet] = useState(null);
    const [chainId, setChainId]                 = useState(null);
    const [balances, setBalances]               = useState({});   // { ETH: '1.23', USDT: '500' }
    const [loading, setLoading]                 = useState(false);
    const [totalUsd, setTotalUsd]               = useState(null);

    // Загружаем кошелёк из профиля при старте
    useEffect(() => {
        userApi.getMe().then(user => {
            if (user?.walletAddress) {
                setConnectedWallet(user.walletAddress);
                setChainId(user.walletChainId || null);
            }
        }).catch(() => {});
    }, []);

    // Получаем провайдер: MetaMask если есть, иначе hardhat нода
    const getProvider = useCallback(() => {
        if (typeof window !== 'undefined' && window.ethereum) {
            return new ethers.BrowserProvider(window.ethereum);
        }
        return null; // HTTP RPC нельзя вызывать с HTTPS страницы (mixed content)
    }, []);

    // Читаем балансы с блокчейна
    const fetchBalances = useCallback(async (address, chain) => {
        if (!address) return;
        const provider = getProvider();
        if (!provider) return; // нет MetaMask — нечем читать балансы
        setLoading(true);
        try {
            const network   = await provider.getNetwork();
            const netChain  = String(network.chainId);

            const tokens = TOKEN_CONFIG[netChain] || TOKEN_CONFIG['31337'];
            const result = {};

            await Promise.all(tokens.map(async (token) => {
                try {
                    if (token.native) {
                        const raw = await provider.getBalance(address);
                        result[token.symbol] = ethers.formatEther(raw);
                    } else {
                        const contract = new ethers.Contract(token.address, ERC20_ABI, provider);
                        const [raw, decimals] = await Promise.all([
                            contract.balanceOf(address),
                            contract.decimals(),
                        ]);
                        result[token.symbol] = ethers.formatUnits(raw, decimals);
                    }
                } catch {
                    result[token.symbol] = '0';
                }
            }));

            setBalances(result);
            setChainId(netChain);
        } catch {
            setBalances({});
        } finally {
            setLoading(false);
        }
    }, [getProvider]);

    // Перечитываем балансы при смене кошелька
    useEffect(() => {
        if (connectedWallet) fetchBalances(connectedWallet, chainId);
        else { setBalances({}); setTotalUsd(null); }
    }, [connectedWallet]);

    // Слушаем смену аккаунта / сети в MetaMask
    useEffect(() => {
        if (!window.ethereum) return;
        const onAccounts = ([acc]) => {
            if (acc) fetchBalances(acc, chainId);
            else { setConnectedWallet(null); setBalances({}); }
        };
        const onChain = () => fetchBalances(connectedWallet, chainId);
        window.ethereum.on('accountsChanged', onAccounts);
        window.ethereum.on('chainChanged', onChain);
        return () => {
            window.ethereum.removeListener('accountsChanged', onAccounts);
            window.ethereum.removeListener('chainChanged', onChain);
        };
    }, [connectedWallet, chainId, fetchBalances]);

    // Список токенов для показа (берём из конфига активной сети)
    const activeTokens = TOKEN_CONFIG[String(chainId)] || TOKEN_CONFIG['31337'];

    return (
        <div className="w-full h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className="pt-[24px] pb-[10px] flex-shrink-0">
                <Header
                    leftButtonText="Close"
                    centerTitle="Balance"
                    onLeftClick={() => dispatch(goBack())}
                    useBackIcon={false}
                />
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto w-full px-4 pt-3 pb-2 space-y-3">

                {/* Total balance card */}
                <div className="w-full rounded-2xl bg-white/[0.05] border border-white/[0.08] px-4 py-4">
                    <span className="text-white/40 text-[11px] uppercase tracking-widest">
                        {connectedWallet ? 'Wallet balance' : 'Total balance'}
                    </span>
                    <div className="mt-1 flex items-baseline gap-2">
                        {connectedWallet ? (
                            loading ? (
                                <div className="w-24 h-8 rounded-xl bg-white/10 animate-pulse mt-1" />
                            ) : (
                                <>
                                    <span className="text-white text-[28px] font-bold leading-none">
                                        {fmt(balances['ETH'] || '0', 4)}
                                    </span>
                                    <span className="text-white/30 text-[13px]">ETH</span>
                                </>
                            )
                        ) : (
                            <>
                                <span className="text-white/30 text-[22px] font-bold leading-none">—</span>
                                <span className="text-white/20 text-[12px]">connect wallet</span>
                            </>
                        )}
                    </div>
                    {connectedWallet && !loading && (
                        <button
                            onClick={() => fetchBalances(connectedWallet, chainId)}
                            className="mt-2 text-[11px] text-white/30 flex items-center gap-1 transition active:text-white/60"
                        >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                            </svg>
                            Refresh
                        </button>
                    )}
                </div>

                {/* Asset list */}
                <div className="w-full rounded-2xl bg-white/[0.05] border border-white/[0.08] px-4">
                    {connectedWallet ? (
                        activeTokens.map(token => (
                            <BalanceItem
                                key={token.symbol}
                                icon={token.icon}
                                mini={token.symbol}
                                max={token.name}
                                count={fmt(balances[token.symbol] || '0', 4)}
                                loading={loading}
                            />
                        ))
                    ) : (
                        <div className="py-6 flex flex-col items-center gap-2">
                            <img src={walletIcon} width={28} height={28} className="opacity-20" alt="wallet" />
                            <span className="text-white/25 text-[13px]">Connect wallet to see balances</span>
                        </div>
                    )}
                </div>

            </div>

            <BalanceActionsBar />

        </div>
    );
}
